import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  buscarSumarizacaoEcdApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import SelectFiltro from "../components/SelectFiltro";
import { fmtMesAno, formatarCnpj, formatarDescricaoConta, sumarizacaoEmAndamento } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";
import {
  usePermissoes,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

function competenciaYm(competencia) {
  return String(competencia || "").slice(0, 7);
}

function labelConta(linha) {
  const desc = formatarDescricaoConta((linha.descricaoConta || "").trim());
  return desc ? `${linha.codCta} — ${desc}` : linha.codCta;
}

/**
 * Código da conta em negrito + descrição em peso normal (destaque igual ao protótipo).
 * O código NÃO é pontuado (ex.: 1.1.01.001): não existe campo de nível/hierarquia na
 * sumarização e o dado real convive com dois planos de contas de tamanhos diferentes
 * (7 e 9 dígitos) — máscara fixa quebraria os de 7. Pontuar exigiria a segmentação do
 * plano de contas (I050 NIVEL/COD_CTA_SUP), que hoje não vem do backend. A descrição (que
 * não depende do tamanho do código) é reformatada em Title Case — igual ao protótipo — via
 * formatarDescricaoConta.
 */
function CelulaConta({ linha }) {
  const desc = formatarDescricaoConta((linha.descricaoConta || "").trim());
  return (
    <>
      <strong>{linha.codCta}</strong>
      {desc ? ` — ${desc}` : ""}
    </>
  );
}

/**
 * TXM-598 — 4 cards do dashboard: Total de débitos, Total de créditos, Saldo final
 * consolidado, Contas com movimento (todos "no filtro" — ver spec do card).
 *
 * "Saldo final consolidado": os valores já chegam SIGNED do backend (débito positivo,
 * crédito negativo — ver comentário em ecd_layout.sql/preencher_ecd). Somar direto é o
 * cálculo contabilmente correto (a soma de TODAS as contas de um razão fechado tende a
 * zero — validado com dado real: R$ 0,00 exato em dez/2023 para o CNPJ 19043440000154).
 * Diferente do protótipo, que soma valores absolutos e por isso não fecha.
 */
function calcularTotais(linhas) {
  const totais = linhas.reduce(
    (acc, linha) => {
      acc.totalDebitos += Number(linha.vlDeb) || 0;
      acc.totalCreditos += Number(linha.vlCred) || 0;
      acc.saldoFinalConsolidado += Number(linha.vlSldFin) || 0;
      return acc;
    },
    { totalDebitos: 0, totalCreditos: 0, saldoFinalConsolidado: 0 }
  );
  const contasDistintas = new Set(linhas.map((linha) => linha.codCta));
  return { ...totais, contasComMovimento: contasDistintas.size };
}

export default function AbaEcd({ apuracaoId, onTotaisChange, bare }) {
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [cnpj, setCnpj] = useState("");
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [contaFiltro, setContaFiltro] = useState("");
  const [competenciaFiltro, setCompetenciaFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarSumarizacaoEcdApuracao(apuracaoId);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setCnpj(res.apuracao?.cnpj || "");
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [apuracaoId]);

  /** TXM-418 — a apuração ainda é de 1 CNPJ (grupo econômico multi-CNPJ é o TXM-601);
   * o filtro existe pra equalizar com o protótipo, hoje com uma opção só. */
  const cnpjOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...(cnpj ? [{ value: cnpj, label: formatarCnpj(cnpj) }] : []),
    ],
    [cnpj]
  );

  const contasDisponiveis = useMemo(() => {
    const mapa = new Map();
    linhas.forEach((linha) => {
      if (!mapa.has(linha.codCta)) mapa.set(linha.codCta, labelConta(linha));
    });
    return Array.from(mapa.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [linhas]);

  const competenciasDisponiveis = useMemo(() => {
    const set = new Set(linhas.map((linha) => competenciaYm(linha.competencia)));
    return Array.from(set).sort();
  }, [linhas]);

  const contaOpcoes = useMemo(
    () => [
      { value: "", label: "Todas" },
      ...contasDisponiveis.map(([cod, label]) => ({ value: cod, label })),
    ],
    [contasDisponiveis]
  );

  const competenciaOpcoes = useMemo(
    () => [
      { value: "", label: "Todas" },
      ...competenciasDisponiveis.map((ym) => ({ value: ym, label: fmtMesAno(ym) })),
    ],
    [competenciasDisponiveis]
  );

  const linhasFiltradas = useMemo(() => {
    let base = linhas;
    if (cnpjFiltro && cnpjFiltro !== cnpj) {
      base = [];
    }
    if (contaFiltro) {
      base = base.filter((linha) => linha.codCta === contaFiltro);
    }
    if (competenciaFiltro) {
      base = base.filter((linha) => competenciaYm(linha.competencia) === competenciaFiltro);
    }
    return [...base].sort((a, b) => {
      const cmp = a.codCta.localeCompare(b.codCta);
      return cmp !== 0 ? cmp : competenciaYm(a.competencia).localeCompare(competenciaYm(b.competencia));
    });
  }, [linhas, cnpj, cnpjFiltro, contaFiltro, competenciaFiltro]);

  const totais = useMemo(() => calcularTotais(linhasFiltradas), [linhasFiltradas]);

  useEffect(() => {
    onTotaisChange?.(totais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais]);

  // Enfileira e acompanha o job até o download (antes só enfileirava e o arquivo nunca
  // chegava ao usuário) — ver Detalhe/hooks/useExportacaoAssincrona.js.
  const exportacao = useExportacaoAssincrona({
    apuracaoId,
    chave: "agrupado",
    enfileirar: () =>
      exportarSumarizacaoApuracao(apuracaoId, { tipo: "agrupado", formato: "csv" }),
    onFinalizar: ({ severity, msg }) => setInfoSnack({ open: true, msg, severity }),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <ApuracaoCardShell
        bare={bare}
        conteudoComScrollProprio
        Icone={MenuBookIcon}
        titulo="Razão contábil por conta e competência"
        tituloSx={{ fontSize: "1.25rem" }}
        acaoHeader={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {linhasFiltradas.length} registros no filtro
            </Typography>
            <BotaoExportacaoProgresso
              progresso={exportacao}
              rotulo="Exportar CSV"
              variant="contained"
              size="small"
              disabled={!podeExportar}
              onClick={() => exportacao.iniciar()}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
            />
          </Box>
        }
        filtros={
          <>
            <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} />
            <SelectFiltro label="Conta contábil" value={contaFiltro} onChange={setContaFiltro} options={contaOpcoes} />
            <SelectFiltro label="Competência" value={competenciaFiltro} onChange={setCompetenciaFiltro} options={competenciaOpcoes} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setCnpjFiltro("");
                setContaFiltro("");
                setCompetenciaFiltro("");
              }}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
            >
              Limpar filtros
            </Button>
          </>
        }
        paginacao={
          linhasFiltradas.length > 0 && (
            <Pagination
              paginationKey="aba-ecd"
              data={linhasFiltradas}
              filteredData={linhasPagina}
              setFilteredData={setLinhasPagina}
            />
          )
        }
      >
        {linhasFiltradas.length === 0 ? (
          <p className={styles.emptyMsg}>Nenhum registro encontrado com os filtros informados.</p>
        ) : (
          <div style={{ margin: "8px 0 0", overflow: "auto" }}>
          <table className={tableStyles.accordion_table}>
            <thead>
              <tr>
                <th scope="col">CNPJ</th>
                <th scope="col">Conta contábil</th>
                <th scope="col">Competência</th>
                <th scope="col" style={NUM_TH}>Saldo inicial</th>
                <th scope="col" style={NUM_TH}>Débitos</th>
                <th scope="col" style={NUM_TH}>Créditos</th>
                <th scope="col" style={NUM_TH}>Saldo final</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhasPagina.map((linha) => (
                <tr key={`${linha.codCta}-${linha.competencia}`}>
                  <td>{formatarCnpj(cnpj)}</td>
                  <td><CelulaConta linha={linha} /></td>
                  <td>{fmtMesAno(competenciaYm(linha.competencia))}</td>
                  <td style={NUM_TD}>{formatarMoedaBRL(linha.vlSldIni)}</td>
                  <td style={NUM_TD}>{formatarMoedaBRL(linha.vlDeb)}</td>
                  <td style={NUM_TD}>{formatarMoedaBRL(linha.vlCred)}</td>
                  <td style={NUM_TD}>{formatarMoedaBRL(linha.vlSldFin)}</td>
                  <td>
                    <button
                      type="button"
                      className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                      onClick={() =>
                        navigate(
                          `/relatorios/apuracoes/${apuracaoId}/detalhe/icms/lancamentos-ecd` +
                            `?conta=${encodeURIComponent(linha.codCta)}&comp=${competenciaYm(linha.competencia)}`
                        )
                      }
                    >
                      Detalhes
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </ApuracaoCardShell>

      <Snackbar
        open={infoSnack.open}
        autoHideDuration={4000}
        onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
      </Snackbar>
    </>
  );
}
